import { useContext, useEffect, useState } from 'react';

import { propertyUtils } from '@zg-rentals/rental-manager-env';

import { useSelector } from '../../../selectors/useSelector';

import { DISPLAY_SERVER_ERROR_AT } from '../../../constants/WizardConstants';

import { WizardContext } from '../../../components/Wizard/context';
import PageWizard from '../../../components/Wizard/PageWizard';
import WizardAlert from '../../../components/WizardAlert';
import { ModularListingContext } from '../../../containers/CreateListingPage/context';
import AmenitiesWithAdditionalAmenitiesFields from './components/AmenitiesWithAdditionalAmenitiesFields';

import { WizardBody, WizardFlexContainer, WizardFormSectionContainer, WizardHeading } from '../../../styles/wizard';

import type { ReactNode } from 'react';
import type { PageWizardProps } from '../../../components/Wizard/PageWizard';

type Props = {
  onGoToPrevStep: () => void;
  onGoToNextStep: () => void;
  onExitWizard: () => void;
  wizardTitle: ReactNode;
  progressStepper: ReactNode;
  totalSteps: number;
  currentStepIndex: number;
  showDesktopViewBreakpoint: PageWizardProps['showDesktopViewBreakpoint'];
};

const AmenitiesProperty = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
  ...rest
}: Props): JSX.Element => {
  const {
    // @ts-expect-error
    wizardState: { isFetchingData },
  } = useContext(WizardContext);

  const {
    modularListingState: { serverErrorObj },
  } = useContext(ModularListingContext);

  const currentProperty = useSelector((state) => state.properties.currentProperty);

  const [clickedNext, setClickedNext] = useState(false);
  const [clickedBackNext, setClickedBack] = useState(false);

  const isRoomForRent = propertyUtils.isRoomForRent({ currentProperty });

  const heading = isRoomForRent ? 'Now tell us more about the property' : 'Now tell us more about your property';

  const bodyContent = isRoomForRent
    ? 'Sharing more will help renters see themselves in the home.'
    : 'Sharing more will help renters see themselves in your home.';

  useEffect(() => {
    const { fieldName } = serverErrorObj;

    if (!fieldName && !isFetchingData) {
      checkNavToStep();
    }
  }, [isFetchingData]);

  const checkNavToStep = () => {
    if (clickedNext) {
      onNextClick();
    }

    if (clickedBackNext) {
      onBackClick();
    }
  };

  const onNextClick = () => {
    if (isFetchingData) {
      setClickedNext(true);
      return;
    }

    onGoToNextStep();
  };

  const onBackClick = () => {
    if (isFetchingData) {
      setClickedBack(true);
      return;
    }

    onGoToPrevStep();
  };

  return (
    <PageWizard
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      onNextClick={onNextClick}
      onBackClick={onBackClick}
      onExitTriggered={onExitWizard}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint}
      {...rest}
    >
      <div data-testid="AmenitiesProperty">
        <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.PAGE} />

        <WizardFlexContainer>
          <WizardFormSectionContainer data-testid="ContentContainer">
            <WizardHeading level={1} textStyle="heading-sm">
              {heading}
            </WizardHeading>

            <WizardBody>{bodyContent}</WizardBody>

            <AmenitiesWithAdditionalAmenitiesFields />
          </WizardFormSectionContainer>
        </WizardFlexContainer>
      </div>
    </PageWizard>
  );
};

export default AmenitiesProperty;
